import { ArrowLeft, Pencil, Send, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { accedeModule } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsRequisition, useRequisition } from '@/api/hooks';
import type { DecisionApi } from '@/api/types';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, ControlesListe, DecisionDialog, ErreurBloc, EtapeBadge, Historique, PageHeader, StatutBadge } from '@/components/common';
import { BandeauCorrection } from '@/components/documents';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateHeureFr, montant } from '@/lib/utils';

/** Détail d'une réquisition (v1.1, § 10) : besoin, articles, circuit, contrôle budgétaire, bons de commande rattachés. */
export function RequisitionDetail() {
  const { id } = useParams();
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useRequisition(id);
  const actions = useActionsRequisition();
  const [dialogue, setDialogue] = useState<'decision' | 'soumettre' | 'resoumettre' | null>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { requisition: r, circuit, actions: a } = data;
  const erreur = (e: unknown) => {
    const c = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
  };
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const derniere = circuit?.steps.at(-1)?.key;
  const decider = (decision: DecisionApi, motif?: string) => executer(() => actions.decider.mutateAsync({ id: r.id, decision, motif }), decision === 'VALIDER' && a.etape === derniere ? 'Réquisition autorisée.' : 'Décision enregistrée.');

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/requisitions" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Réquisitions
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {r.numero ?? 'Réquisition (brouillon)'} <StatutBadge statut={r.statut} /> {r.statut === 'SOUMIS' && <EtapeBadge etape={data.etapeCourante} />}
            {r.urgence && <Badge variant="destructive">urgente</Badge>}
            {r.memorial && <Badge variant="outline">Activité du Mémorial</Badge>}
          </span>
        }
        sousTitre={
          <>
            {r.objet} · pour {r.structure?.libelle} · émise par {r.emetteur?.prenom} {r.emetteur?.nom}
            {r.dateSoumission ? ` · soumise le ${dateHeureFr(r.dateSoumission)}` : ''}
            {r.dateAutorisation ? ` · autorisée le ${dateHeureFr(r.dateAutorisation)}` : ''}
          </>
        }
        actions={
          <>
            <BoutonPdf chemin={`/pdf/requisitions/${r.id}`} nom={`${r.numero ?? 'REQ'}.pdf`} />
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/requisitions/${r.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {a.commander && (
              <Button variant="secondary" asChild>
                <Link to={`/achats/nouveau?requisition=${r.id}`}>
                  <ShoppingCart className="size-4" /> Préparer le bon de commande
                </Link>
              </Button>
            )}
          </>
        }
      />
      <BandeauCorrection statut={r.statut} correction={data.correction} motif={r.motifAnnulation} automatique={r.annulationAutomatique} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-primary/5 p-4">
              <div className="text-xs uppercase text-muted-foreground">Montant estimé</div>
              <div className="num text-xl font-semibold">{montant(r.montantEstime, true)}</div>
              <div className="text-xs text-muted-foreground">{r.articles.length} article{r.articles.length > 1 ? 's' : ''}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Sous-activité</div>
              <div className="font-semibold">{r.ligneBudgetaire?.code}</div>
              <div className="text-xs text-muted-foreground">{r.ligneBudgetaire?.libelle}</div>
              {r.ligneBudgetaire?.id && accedeModule(u.profils, 'plan') && (
                <Link to={`/plan/${r.ligneBudgetaire.id}`} className="mt-1 block text-xs text-primary hover:underline">
                  Fiche de suivi de la sous-activité
                </Link>
              )}
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Délai souhaité</div>
              <div className="text-sm font-medium">{r.delaiSouhaite ?? '—'}</div>
              {r.urgence && <div className="text-xs text-red-700">{r.justificationUrgence}</div>}
            </div>
          </div>

          {data.budget && (
            <Alert variant={data.budget.ok ? 'default' : 'destructive'}>
              <AlertTitle>{data.budget.ok ? 'Disponibilité budgétaire confirmée' : 'Dépassement budgétaire'}</AlertTitle>
              <AlertDescription>
                {data.budget.message} Disponible {montant(data.budget.disponible)} · après circuit {montant(data.budget.disponiblePrevisionnel)}.
              </AlertDescription>
            </Alert>
          )}

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesListe controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Justification du besoin</CardTitle>
            </CardHeader>
            <CardContent className="whitespace-pre-line text-sm">{r.justification || <span className="text-muted-foreground">À renseigner avant la soumission.</span>}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Articles ou prestations demandés</CardTitle>
              <CardDescription>Estimations fournies par le service ; le prix est fixé par le bon de commande après consultation des fournisseurs.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Désignation</TableHead>
                    <TableHead className="text-right">Quantité</TableHead>
                    <TableHead className="text-right">Estimation unitaire</TableHead>
                    <TableHead className="text-right">Total estimé</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {r.articles.map((x, i) => (
                    <TableRow key={i}>
                      <TableCell>{x.designation}</TableCell>
                      <TableCell className="num text-right">
                        {x.quantite} {x.unite}
                      </TableCell>
                      <TableCell className="num text-right">{montant(x.estimationUsd, true)}</TableCell>
                      <TableCell className="num text-right">{montant(x.quantite * x.estimationUsd, true)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-medium">
                      Total estimé (USD)
                    </TableCell>
                    <TableCell className="num text-right font-semibold">{montant(r.montantEstime, true)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <PiecesJointes objetType="REQ" objetId={r.id} typesSuggeres={['PROFORMA', 'JUSTIFICATIF', 'AUTRE']} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {circuit && <CardDescription>Directeur → Contrôle de gestion → Responsable des achats, puis DGA Finances et Directeur Général selon le montant.</CardDescription>}
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={circuit} />
            </CardContent>
          </Card>
          {data.bonsCommande.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Bons de commande</CardTitle>
                <CardDescription>Le bon soumis prend le relais de la réquisition dans le disponible après circuit.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.bonsCommande.map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/achats/${b.id}`} className="font-medium text-primary hover:underline">
                      {b.numero ?? 'Bon de commande (brouillon)'}
                    </Link>
                    <span className="truncate text-muted-foreground">{b.fournisseur}</span>
                    <StatutBadge statut={b.statut} />
                    <span className="num">{montant(b.montantUsd, true)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog
        ouvert={dialogue === 'soumettre'}
        onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)}
        titre="Soumettre la réquisition"
        libelle="Soumettre"
        description="Un numéro sera attribué et le circuit ouvert : Directeur (Coordonnateur du Mémorial avant lui pour une activité du Mémorial), contrôle de gestion, Responsable des achats, puis DGA Finances et Directeur Général selon le montant. Le montant estimé réserve le budget."
        onConfirmer={() => executer(() => actions.soumettre.mutateAsync(r.id), 'Réquisition soumise.')}
        chargement={actions.soumettre.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'resoumettre'}
        onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)}
        titre="Soumettre à nouveau"
        libelle="Soumettre à nouveau"
        onConfirmer={(reponse) => executer(() => actions.resoumettre.mutateAsync({ id: r.id, reponse }), 'Réquisition soumise à nouveau.')}
        chargement={actions.resoumettre.isPending}
      />
    </div>
  );
}
